/**
 * Le climat de référence du catalogue (Belgique, nord de la France) : celui
 * qu'on suppose quand le climat du jardin est inconnu (pas d'adresse, API en
 * panne).
 */
export const REFERENCE_CLIMATE = 'Cfb';

/**
 * Pour chaque climat de Köppen, les autres du plus proche au plus lointain,
 * vus du potager : le régime des températures d'abord (gel, longueur de la
 * saison), la saison sèche ensuite. Un Bruxellois (Cfb) est mieux servi par
 * une variante lyonnaise (Cfa) que par une variante moscovite (Dfb) ; un
 * Marseillais (Csa) par une variante portugaise (Csb) que par une variante
 * lyonnaise. Les climats absents d'une liste viennent après elle (voir
 * `climateDistance`).
 */
const CLIMATE_NEIGHBOURS: Record<string, string[]> = {
  // A (tropical)
  Af: ['Am', 'Aw', 'As'],
  Am: ['Af', 'Aw', 'As'],
  Aw: ['As', 'Am', 'Af', 'BWh'],
  As: ['Aw', 'Am', 'Af', 'BWh'],
  // B (aride)
  BWh: ['BWk', 'Csa', 'Aw', 'As'],
  BWk: ['BWh', 'Csa', 'Dsa', 'Cfa'],
  // C (tempéré)
  Csa: ['Csb', 'Cfa', 'Cfb', 'Dsa', 'BWk', 'BWh', 'Dfa'],
  Csb: ['Csa', 'Cfb', 'Cfa', 'Dsb', 'Dfb', 'Cfc'],
  Cfa: ['Cfb', 'Csa', 'Dfa', 'Csb', 'Dwa', 'Dsa', 'Dfb'],
  Cfb: ['Cfa', 'Csb', 'Dfb', 'Cfc', 'Csa', 'Dfa', 'Dsb', 'Dwb', 'Dfc'],
  Cfc: ['Cfb', 'Dfc', 'Dfb', 'Csb', 'ET', 'Cfa'],
  // D (continental)
  Dfa: ['Dfb', 'Cfa', 'Dwa', 'Dsa', 'Cfb', 'Dfc'],
  Dfb: ['Cfb', 'Dfc', 'Dfa', 'Dwb', 'Dsb', 'Cfa', 'Cfc'],
  Dfc: ['Dfb', 'Cfc', 'Dwc', 'Dsc', 'ET', 'Cfb'],
  Dsa: ['Dsb', 'Csa', 'Dfa', 'Cfa', 'BWk'],
  Dsb: ['Dsa', 'Csb', 'Dfb', 'Dsc', 'Cfb'],
  Dsc: ['Dsb', 'Dfc', 'Dwc', 'ET'],
  Dwa: ['Dfa', 'Dwb', 'Cfa', 'Dsa'],
  Dwb: ['Dfb', 'Dwa', 'Dwc', 'Cfb'],
  Dwc: ['Dfc', 'Dwb', 'Dwd', 'Dsc'],
  Dwd: ['Dwc', 'Dfc', 'ET'],
  // E (polaire)
  ET: ['Cfc', 'Dfc', 'Dwd', 'EF'],
  EF: ['ET'],
};

/**
 * L'éloignement d'un climat vu d'un autre : 0 pour le même, puis son rang
 * dans les voisins. Hors des voisins, les lettres de Köppen en commun
 * départagent (Cwb partage « Cw » avec Cwa, « C » avec Cfb).
 */
export function climateDistance(from: string, to: string): number {
  if (from === to) return 0;
  const rank = CLIMATE_NEIGHBOURS[from]?.indexOf(to) ?? -1;
  if (rank >= 0) return rank + 1;
  let common = 0;
  while (common < Math.min(from.length, to.length) && from[common] === to[common]) common++;
  return 100 - common;
}

/**
 * La variante d'une culture faite pour le climat du jardin, sinon celle du
 * climat le plus proche ; jamais la première venue. Une variante sans climat
 * vaut pour tous : elle passe juste après celle du climat exact. À
 * éloignement égal, l'ordre de la liste départage. Climat inconnu : celui de
 * référence du catalogue.
 */
export function nearestClimateVariant<T extends { climateType?: string | null }>(
  variants: T[] | null | undefined,
  climate: string | null | undefined,
): T | undefined {
  const target = climate || REFERENCE_CLIMATE;
  const distance = (variant: T): number => {
    if (!variant.climateType) return 0.5;
    return climateDistance(target, variant.climateType);
  };
  let best: T | undefined;
  for (const variant of variants ?? []) {
    if (!best || distance(variant) < distance(best)) best = variant;
  }
  return best;
}
